import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip, Input, Popover } from 'freemium-ui';
import { HexColorPicker } from 'react-colorful';
import styled from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import { StyledPicker } from './style';
import { useOnClickOutside } from 'utils/hooks';
import { StyledDiv } from './SurveySettings';
import Icons from 'components/icons';
import UploadLogo from 'components/upload-logo';
import { UiThemeType } from 'shared/types';

export type CustomizeProps = {
  imageRef: any;
};

const THEME_SETTINGS: {
  id: keyof UiThemeType;
  label: string;
  default: string;
}[] = [
  {
    id: 'brand_color',
    label: 'Primary color',
    default: '#264966',
  },
  {
    id: 'primary_button_text_color',
    label: 'Button text color',
    default: '#111',
  },
  {
    id: 'body_background_color',
    label: 'Survey background color',
    default: '#ebeff3',
  },

  // {
  //   id: 'button_style',
  //   label: 'Button style',
  //   type: 'input',
  // },
  // {
  //   id: 'secondary_background',
  //   label: 'Secondary button color',
  //   default: '#efefef',
  // },
  // {
  //   id: 'secondary_button_text_color',
  //   label: 'Secondary button text Color',
  //   default: '#111',
  // },
  {
    id: 'question_background_color',
    label: `Question background color`,
    default: '#fff',
  },
  {
    id: 'question_text_color',
    label: `Question text color`,
    default: '#111',
  },
];

export const DesignMenu = ({ imageRef }: CustomizeProps) => {
  const { id }: { id: string } = useParams();
  const [anchorEl, setAnchorEl] = React.useState<any>(null);
  const {
    register,
    formState: { errors },
    control,
    getValues,
    watch,
    setValue,
  } = useFormContext();

  const itemRef = useRef(null);
  const {
    id: surveyId,
    ui_branding,
    meta: { survey_layout },
    state,
  } = getValues();

  const ui_theme = watch('ui_theme');

  React.useEffect(() => {
    if (imageRef && imageRef.current && itemRef && itemRef.current)
      imageRef.current?.addEventListener('click', () =>
        setAnchorEl(itemRef.current),
      );
    return () => {
      imageRef.current?.removeEventListener('click', () => setAnchorEl(null));
    };
  }, []);

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
    });
  };

  const isActive = state === 'ACTIVE';

  return (
    <div>
      <div
        aria-describedby="basic-popover"
        ref={itemRef}
        onClick={(e: any) => setAnchorEl(e.currentTarget)}
      >
        <Tooltip label="Design settings" position="left">
          <Icons glyph="paint" size="22" viewBox="-8 -10 26 26" />
        </Tooltip>
      </div>
      <Popover
        id="basic-popover"
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <StyledDiv onClick={(e: any) => e.stopPropagation()}>
          <div className="content">
            <div className="header">Design</div>
            <div className="field">
              <UploadLogo
                fieldId="ui_branding.logo_url"
                surveyId={id || surveyId || null}
                form={useFormContext()}
                label="Logo URL"
                logoType="survey"
                isDisabled={isActive}
              />
            </div>
            <div className="field">
              <label htmlFor="style" className="is-required">
                Display title
                <Tooltip
                  label="This name will appear when logo is not added"
                  position="top"
                >
                  <Icons
                    glyph="info"
                    size="12"
                    height="16"
                    width="16"
                    viewBox="0 0 16 16"
                  />
                </Tooltip>
              </label>
              <Input
                disabled={isActive}
                inputStyle={{
                  minWidth: '110px',
                }}
                value={watch('ui_branding.name')}
                onChange={(e: any) =>
                  setValue('ui_branding.name', e.target.value)
                }
                maxLength="50"
              />
            </div>
            <div className="field">
              <UploadLogo
                fieldId="ui_branding.favicon_url"
                surveyId={id || surveyId || null}
                form={useFormContext()}
                label="Favicon"
                logoType="favicon"
                isDisabled={isActive}
              />
            </div>
            <div className="field">
              <label htmlFor="style" className="is-required">
                Survey layout
                <Tooltip
                  label="You can experience the layout in the 'Preview'"
                  position="top"
                >
                  <Icons
                    glyph="info"
                    size="12"
                    height="16"
                    width="16"
                    viewBox="0 0 16 16"
                  />
                </Tooltip>
              </label>
              <FormButtonMenu
                name="meta.survey_layout"
                control={control}
                disabled={isActive}
                defaultValue={survey_layout}
              />
            </div>
            {THEME_SETTINGS.map((item: any) => (
              <div className="field " key={item.id}>
                {item.type === 'input' ? (
                  <Input
                    label={item.label}
                    {...register(`ui_theme.${item.id}`)}
                    isErrored={!!(errors?.ui_theme && errors.ui_theme[item.id])}
                    errorText={
                      errors?.ui_theme && errors.ui_theme[item.id]?.message
                    }
                    disabled={isActive}
                    inputStyle={{
                      minWidth: '110px',
                    }}
                  />
                ) : (
                  <>
                    <label className="is-required" htmlFor="lowRating">
                      {item.label}
                    </label>
                    <ColorPicker
                      color={ui_theme?.[item.id] || item.default}
                      onChange={(newColor: string) =>
                        setFormValue(`ui_theme.${item.id}`, newColor)
                      }
                      disabled={isActive}
                      isErrored={
                        !!(errors?.ui_theme && errors.ui_theme[item.id])
                      }
                      errorText={
                        errors?.ui_theme && errors.ui_theme[item.id]?.message
                      }
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </StyledDiv>
      </Popover>
    </div>
  );
};

export default DesignMenu;

const FormButtonMenu = ({ control, name, disabled, ...rest }: any): any => {
  const { field } = useController({
    name,
    control,
    defaultValue: rest.defaultValue,
  });
  return (
    <ButtonMenu
      active={field.value}
      options={[
        {
          id: 'card',
          icon: 'card-style',
          viewBox: '0 -5 26 26',
          label: 'Card',
        },
        {
          id: 'standard',
          icon: 'classic-style',
          label: 'Classic',
          viewBox: '0 -2 20 20',
        },
      ]}
      onChange={(value: any) => {
        if (!disabled) field.onChange(value);
      }}
    />
  );
};

export const ColorPicker = ({
  color,
  isErrored,
  label,
  errorText,
  disabled,
  onChange,
}: {
  label?: string;
  color: string;
  isErrored?: boolean;
  disabled?: boolean;
  errorText?: string;
  onChange: (newColor: string) => void;
}) => {
  const componentRef = useRef<HTMLDivElement>(null);

  const [pickerOpen, setPickerOpen] = React.useState<boolean>(false);

  useOnClickOutside(componentRef, () => {
    setPickerOpen(false);
  });

  return (
    <div ref={componentRef}>
      <Input
        startAdornment={
          <StyledPicker color={color}>
            <div
              onClick={() => !disabled && setPickerOpen(true)}
              className="color-preview"
            />
            <div className={`picker ${pickerOpen ? 'open' : ''}`}>
              <HexColorPicker color={color} onChange={onChange} />
            </div>
          </StyledPicker>
        }
        value={color}
        inputStyle={{
          minWidth: '110px',
        }}
        disabled={disabled}
        label={label}
        isErrored={isErrored}
        errorText={errorText}
        onChange={(e: any) => {
          onChange(e.target.value);
        }}
      />
    </div>
  );
};

const ButtonMenu = ({
  options,
  active,
  onChange,
}: {
  options: any[];
  active: any;
  onChange: Function;
}) => {
  return (
    <StyledButtonMenu>
      <div className="btn-menu">
        {options.map((option: any) => (
          <div
            key={option.id}
            className={`${option.id === active ? 'active' : ''} ${
              option.id
            } btn-value`}
            onClick={(e: any) => {
              e.stopPropagation();
              onChange(option.id);
            }}
          >
            {option.icon && (
              <Icons
                size="20"
                viewBox={option.viewBox || '0 0 16 16'}
                glyph={option.icon}
              />
            )}
            <span className="label">{option.label}</span>
          </div>
        ))}
      </div>
    </StyledButtonMenu>
  );
};

const StyledButtonMenu = styled('div')`
  .btn-menu {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 3px;
  }

  .btn-value:hover {
    border: 1px solid var(--color-smoke-300);
  }
  .btn-value.active {
    border-radius: 4px;
    border: 1px solid #12344d;
    box-shadow: inset 0px 1px 2px rgba(18, 52, 77, 0.08);
    border-radius: 4px;
  }

  .btn-value {
    border-radius: 4px;
    display: flex;
    width: calc(50% - 10px);
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    padding: 8px 12px;
    cursor: pointer;
    background: #ffffff;
    border: 1px solid #f2f2f2;
    box-shadow: inset 0px 1px 2px rgba(18, 52, 77, 0.08);
    border-radius: 4px;
  }

  #card-style {
    fill: none;
  }
`;
